/**
 * Naseptavac vyhledavani
 *
 * Pri psani do vyhledavaciho pole vola endpoint getSuggestJson
 * (eshop_catalog_controller_ajax) a pod polem vykresli vysledky
 * rozdelene na produkty, kategorie, vyrobce a stitky.
 *
 * Pouziti: <form data-search-suggest="URL endpointu"> s inputem [name=search]
 */

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 250;

class SearchSuggest {

	constructor(form) {
		this.form = form;
		this.input = form.querySelector('input[name="search"]');
		this.endpoint = form.dataset.searchSuggest;
		this.cache = new Map();
		this.controller = null;
		this.timer = null;
		this.query = '';
		this.activeIndex = -1;

		this.box = document.createElement('div');
		this.box.className = 'search-suggest';
		this.box.id = 'search-suggest';
		this.box.hidden = true;
		form.appendChild(this.box);

		this.input.setAttribute('autocomplete', 'off');
		this.input.setAttribute('role', 'combobox');
		this.input.setAttribute('aria-autocomplete', 'list');
		this.input.setAttribute('aria-controls', this.box.id);
		this.input.setAttribute('aria-expanded', 'false');

		this.bindEvents();
	}

	bindEvents() {
		this.input.addEventListener('input', () => this.schedule());
		this.input.addEventListener('focus', () => {
			if (this.box.innerHTML !== '' && this.input.value.trim() === this.query) {
				this.open();
			}
		});
		this.input.addEventListener('keydown', (e) => this.onKeyDown(e));

		document.addEventListener('click', (e) => {
			if (!this.form.contains(e.target)) {
				this.close();
			}
		});
	}

	schedule() {
		clearTimeout(this.timer);
		const query = this.input.value.trim();

		if (query.length < MIN_LENGTH) {
			this.abort();
			this.query = '';
			this.close();
			return;
		}

		this.timer = setTimeout(() => this.load(query), DEBOUNCE_MS);
	}

	abort() {
		if (this.controller) {
			this.controller.abort();
			this.controller = null;
		}
	}

	load(query) {
		if (query === this.query && !this.box.hidden) {
			return;
		}

		if (this.cache.has(query)) {
			this.render(query, this.cache.get(query));
			return;
		}

		this.abort();
		this.controller = new AbortController();
		this.form.classList.add('search-form--loading');

		const url = this.endpoint + (this.endpoint.indexOf('?') === -1 ? '?' : '&') + 'search=' + encodeURIComponent(query);

		fetch(url, {
			signal: this.controller.signal,
			headers: {'X-Requested-With': 'XMLHttpRequest'},
		})
			.then((response) => response.json())
			.then((json) => {
				if (!json.success) {
					throw new Error(json.error || 'Suggest error');
				}
				this.cache.set(query, json.data);
				// mezitim se mohl text v poli zmenit
				if (this.input.value.trim() === query) {
					this.render(query, json.data);
				}
			})
			.catch((error) => {
				if (error.name !== 'AbortError') {
					console.log('search suggest error', error);
				}
			})
			.finally(() => {
				this.form.classList.remove('search-form--loading');
			});
	}

	/**
	 * URL vysledku vyhledavani (stejna jako po odeslani formulare)
	 */
	searchUrl(query) {
		const action = this.form.getAttribute('action');
		return action + (action.indexOf('?') === -1 ? '?' : '&') + 'search=' + encodeURIComponent(query);
	}

	render(query, data) {
		this.query = query;
		this.activeIndex = -1;

		const products = data.products || [];
		const categories = data.categories || [];
		const manufacturers = data.manufacturers || [];
		const tags = data.tags || [];

		let aside = '';
		aside += this.renderSection('Kategorie', categories, (item) => this.renderLink(
			item.url,
			this.highlight(item.title, query) + (item.parentTitle ? '<small class="search-suggest__parent">' + escapeHtml(item.parentTitle) + '</small>' : '')
		));
		aside += this.renderSection('Výrobci', manufacturers, (item) => this.renderLink(this.searchUrl(item.title), this.highlight(item.title, query)));
		aside += this.renderSection('Štítky', tags, (item) => this.renderLink(item.url, this.highlight(item.title, query)));

		const productsHtml = this.renderSection('Produkty', products, (item) => this.renderProduct(item, query));

		let html = '';
		if (aside === '' && productsHtml === '') {
			html += '<div class="search-suggest__empty">Pro „' + escapeHtml(query) + '“ jsme nic nenašli.</div>';
		}
		else {
			html += '<div class="search-suggest__body">';
			if (productsHtml !== '') {
				html += '<div class="search-suggest__products">' + productsHtml + '</div>';
			}
			if (aside !== '') {
				html += '<div class="search-suggest__aside">' + aside + '</div>';
			}
			html += '</div>';
		}

		html += '<a class="search-suggest__all search-suggest__link" href="' + escapeHtml(this.searchUrl(query)) + '">'
			+ 'Zobrazit všechny výsledky pro „' + escapeHtml(query) + '“</a>';

		this.box.innerHTML = html;
		this.box.querySelectorAll('.search-suggest__link').forEach((link, index) => {
			link.id = 'search-suggest-item-' + index;
			link.setAttribute('role', 'option');
		});

		this.open();
	}

	renderSection(title, items, renderItem) {
		if (!items.length) {
			return '';
		}

		return '<div class="search-suggest__section">'
			+ '<div class="search-suggest__heading">' + escapeHtml(title) + '</div>'
			+ '<ul class="search-suggest__list" role="listbox">' + items.map((item) => '<li>' + renderItem(item) + '</li>').join('') + '</ul>'
			+ '</div>';
	}

	renderLink(url, content) {
		return '<a class="search-suggest__link" href="' + escapeHtml(url) + '">' + content + '</a>';
	}

	renderProduct(item, query) {
		const image = item.image
			? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy">'
			: '';

		let meta = '';
		if (item.code) {
			meta += '<span class="search-suggest__code">' + this.highlight(item.code, query) + '</span>';
		}
		if (item.accessType) {
			meta += '<span class="search-suggest__availability ' + escapeHtml(item.accessTypeClass || '') + '">' + escapeHtml(item.accessType) + '</span>';
		}

		return '<a class="search-suggest__link search-suggest__product" href="' + escapeHtml(item.url) + '">'
			+ '<span class="search-suggest__image">' + image + '</span>'
			+ '<span class="search-suggest__info">'
			+ '<span class="search-suggest__title">' + this.highlight(item.title, query) + '</span>'
			+ (meta ? '<span class="search-suggest__meta">' + meta + '</span>' : '')
			+ '</span>'
			+ '<span class="search-suggest__price">' + escapeHtml(decodeEntities(item.price_dph_format || '')) + '</span>'
			+ '</a>';
	}

	/**
	 * Zvyrazneni slov z dotazu (bez ohledu na velikost pismen a diakritiku)
	 */
	highlight(text, query) {
		text = decodeEntities(String(text || ''));
		const words = query.split(/\s+/).filter((word) => word.length >= MIN_LENGTH).map(normalize);
		if (!words.length) {
			return escapeHtml(text);
		}

		// normalizovany text ma stejnou delku jako puvodni, indexy si odpovidaji
		const normalized = normalize(text);
		const marks = new Array(text.length).fill(false);
		words.forEach((word) => {
			let pos = normalized.indexOf(word);
			while (pos !== -1) {
				marks.fill(true, pos, pos + word.length);
				pos = normalized.indexOf(word, pos + word.length);
			}
		});

		let html = '';
		let open = false;
		for (let i = 0; i < text.length; i++) {
			if (marks[i] !== open) {
				html += marks[i] ? '<mark>' : '</mark>';
				open = marks[i];
			}
			html += escapeHtml(text[i]);
		}

		return html + (open ? '</mark>' : '');
	}

	onKeyDown(e) {
		if (this.box.hidden) {
			return;
		}

		const links = this.box.querySelectorAll('.search-suggest__link');

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				this.setActive(links, this.activeIndex + 1 >= links.length ? 0 : this.activeIndex + 1);
				break;
			case 'ArrowUp':
				e.preventDefault();
				this.setActive(links, this.activeIndex - 1 < 0 ? links.length - 1 : this.activeIndex - 1);
				break;
			case 'Enter':
				if (this.activeIndex >= 0 && links[this.activeIndex]) {
					e.preventDefault();
					window.location.href = links[this.activeIndex].href;
				}
				break;
			case 'Escape':
				this.close();
				break;
		}
	}

	setActive(links, index) {
		links.forEach((link) => link.classList.remove('active'));
		this.activeIndex = index;

		const link = links[index];
		if (link) {
			link.classList.add('active');
			link.scrollIntoView({block: 'nearest'});
			this.input.setAttribute('aria-activedescendant', link.id);
		}
	}

	open() {
		this.box.hidden = false;
		this.form.classList.add('search-form--suggest');
		this.input.setAttribute('aria-expanded', 'true');
	}

	close() {
		this.box.hidden = true;
		this.form.classList.remove('search-form--suggest');
		this.input.setAttribute('aria-expanded', 'false');
		this.input.removeAttribute('aria-activedescendant');
	}
}

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
}

/**
 * Prevod HTML entit na text (napr. &nbsp; v cenach), bez spusteni HTML
 */
function decodeEntities(value) {
	if (value.indexOf('&') === -1) {
		return value;
	}

	return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
}

/**
 * Mala pismena bez diakritiky, zachovava delku retezce
 */
function normalize(value) {
	return value
		.split('')
		.map((char) => char.normalize('NFD').replace(/[̀-ͯ]/g, '').charAt(0) || char)
		.join('')
		.toLowerCase();
}

document.addEventListener('DOMContentLoaded', function() {
	document.querySelectorAll('form[data-search-suggest]').forEach((form) => {
		if (form.querySelector('input[name="search"]')) {
			new SearchSuggest(form);
		}
	});
});
